'use client';

import Cookies from 'js-cookie';
import { usePathname } from 'next/navigation';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { i18nConfig } from '../utils';
import { updateLocale as fetchUpdateLocale } from '@/api/common';
import { LANGUAGE_PARAM } from '@/helpers/variables';

export const useLanguageChange = () => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  const pathname = usePathname();

  const [pending, setPending] = useState(false);

  const changeLanguage = useCallback(
    async (newLocale: string) => {
      setPending(true);
      try {
        // Update user locale in the backend
        await fetchUpdateLocale({ locale: newLocale });

        // Update i18n locale
        await i18n.changeLanguage(newLocale);
        Cookies.set(LANGUAGE_PARAM, newLocale, {
          expires: 9999,
          path: '/',
          secure: true,
          sameSite: 'Lax',
        });

        // Redirect to the new locale path
        let href;
        if (currentLang === i18nConfig.defaultLocale) {
          href = '/' + newLocale + pathname;
        } else {
          href = pathname.replace(`/${currentLang}`, `/${newLocale}`);
        }

        // router sometimes does not work and after navigation via Link returns previous locale
        window.location.href = href;
      } catch (error) {
        console.error('Error changing language:', error);
      } finally {
        setPending(false);
      }
    },
    [currentLang, pathname]
  );

  return [changeLanguage, pending] as [typeof changeLanguage, boolean];
};
